import React from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { Typography, Box } from '@mui/material';

import StandardDataGrid from '../../../components/Common/StandardDataGrid';
import ClinicalTrialsModal from '../../../components/DataSections/ClinicalTrials/Modal/ClinicalTrialsModal';
import { useSystemError } from '../../../components/Error/SystemErrorContext';
import { ClinicalTrialsModel } from '../../../models/data-sections/clinical-trials/clinicalTrialsModel';
import { trimDate } from '../../../utils/commonUtils';

interface ClinicalTrialProps {
  searchExecutionId: string;
  studies: ClinicalTrialsModel[] | null;
  isLoading: boolean;
}

const ClinicalTrials: React.FC<ClinicalTrialProps> = ({ searchExecutionId, studies, isLoading }) => {
  const { triggerSystemError } = useSystemError();

  const columns: GridColDef<ClinicalTrialsModel>[] = [
    { field: 'nct_id', headerName: 'NCT ID', width: 125 },
    { field: 'enrollment', headerName: 'Enrollment Count', width: 130, type: 'number' },
    { field: 'overall_status', headerName: 'Status', width: 150 },
    {
      field: 'brief_title',
      headerName: 'Brief Title',
      width: 600,
      renderCell: (params) => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
          {params.value}
        </div>
      ),
    },
    {
      field: 'completion_date',
      headerName: 'Completion Date',
      width: 130,
      renderCell: (params) => <>{trimDate(params.value as string)}</>,
    },
    {
      field: 'start_date',
      headerName: 'Start Date',
      width: 130,
      renderCell: (params) => <>{trimDate(params.value as string)}</>,
    },
    {
      field: 'last_update_posted_date',
      headerName: 'Updated Date',
      width: 130,
      renderCell: (params) => <>{trimDate(params.value as string)}</>,
    },
    { field: 'outcomes_count', headerName: 'Outcomes Count', width: 120, type: 'number' },
    { field: 'phase', headerName: 'Phase', width: 100 },
    {
      field: 'results_first_submitted_date',
      headerName: 'Results Submitted',
      width: 200,
      renderCell: (params) => <>{params.value ? trimDate(params.value) : 'N/A'}</>,
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 300,
      renderCell: (params) => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
          {params.value || 'N/A'}
        </div>
      ),
    },
    {
      field: 'study_first_submitted_date',
      headerName: 'Study Submitted Date',
      width: 200,
      renderCell: (params) => <>{params.value ? trimDate(params.value) : 'N/A'}</>,
    },
    { field: 'sponsors_count', headerName: 'Sponsors Count', width: 150, type: 'number' },
  ];

  const rows = studies?.map((study, index) => ({
    id: index,
    ...study,
  })) || [];

  return (
    <StandardDataGrid
      isLoading={isLoading}
      singleGrid={{
        rows,
        columns,
        modalRenderer: (row) => (
          <ClinicalTrialsModal
            searchExecutionId={searchExecutionId}
            selected_study={row}
            onClose={() => { }}
          />
        ),
      }}
    />
  );
};

export default ClinicalTrials;
